/**
 * @fileoverview
 * This file defines a Dexie database for managing user, project, dataset, datapoint, and file data.
 * @module DexieDB
 */

import Dexie from "dexie";

/**
 * Represents the Dexie database instance.
 * @class
 * @name DexieDB
 */
const db = new Dexie("database");

// Define the database schema with version 10
db.version(17).stores({
  /**
   * Represents the 'users' table in the database.
   * @member {Object}
   * @property {number} ++id - Auto-incremented primary key (given by IndexedDB).
   * @property {string} email - User's email address.
   * @property {string} username - User's username.
   * @property {string} password - User's password.
   * @memberof DexieDB
   */
  users: "++id,email,username,password",

  /**
   * Represents the 'projects' table in the database.
   * @member {Object}
   * @property {number} ++id - Auto-incremented primary key (given by IndexedDB).
   * @property {number} userID - ID of the user associated with the project.
   * @property {string} name - Project name.
   * @property {string} uuid - Universally unique identifier for the project (backend).
   * @memberof DexieDB
   */
  projects: "++id,userID,name,uuid",

  /**
   * Represents the 'datasets' table in the database.
   * @member {Object}
   * @property {number} ++id - Auto-incremented primary key (given by IndexedDB).
   * @property {number} projectID - ID of the project associated with the dataset.
   * @property {string} name - Dataset name.
   * @property {string} schema - Dataset schema.
   * @property {string} uuid - Universally unique identifier for the dataset (backend).
   * @memberof DexieDB
   */
  datasets: "++id,projectID,name,schema,uuid",

  /**
   * Represents the 'datapoints' table in the database.
   * @member {Object}
   * @property {number} ++id - Auto-incremented primary key (given by IndexedDB).
   * @property {number} datasetID - ID of the dataset associated with the datapoint.
   * @property {Object} formdata - Form data associated with the datapoint.
   * @property {string} uuid - Universally unique identifier for the datapoint (backend).
   * @memberof DexieDB
   */
  datapoints: "++id,datasetID,formdata,uuid",

  /**
   * Represents the 'fileData' table in the database.
   * @member {Object}
   * @property {number} ++id - Auto-incremented primary key (given by IndexedDB).
   * @property {number} datapointID - ID of the datapoint associated with the file data.
   * @property {string} label - Label for the file.
   * @property {Blob} data - File data.
   * @property {string} url - S3 URI associated with the file data.
   * @property {Object} metadata - Additional metadata for the file.
   * @memberof DexieDB
   */
  fileData: "++id,datapointID,label,data,url,metadata",



  /**
   * Represents the 'fileData' table in the database.
   * @member {Object}
   * @property {number} ++id - Auto-incremented primary key (given by IndexedDB).
   * @property {number} datasetID - ID of the dataset associated with the datapoint.
   * @property {Object} formdata - Form data associated with the datapoint.
   * @memberof DexieDB
   */

  drafts: "++id,draftId,formData,fileData,datasetId,timestamp"
});

export { db };
